import { useCallback, useState } from 'react';
import { useFinancialData } from '../contexts/FinancialDataContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook for Google Sheets operations
 * Wraps the FinancialDataContext with additional loading states
 */
function isAuthError(err: unknown): boolean {
  const apiError = err as { status?: number; result?: { error?: { code?: number } } };
  const status = apiError.status || apiError.result?.error?.code;
  return status === 401 || status === 403;
}

function isGoogleReconnectError(err: unknown): boolean {
  return err instanceof Error && err.message === 'GOOGLE_RECONNECT_REQUIRED';
}

export const useGoogleSheets = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { loadDataFromSheets, createNewSheet } = useFinancialData();
  const { sheetId, setSheetId, setAppState, setError, isReady, refreshGoogleToken, googleTokenFailed } = useAuth();

  const refreshData = useCallback(async () => {
    if (!sheetId) {
      setError('No sheet connected');
      return;
    }

    setIsRefreshing(true);
    try {
      const result = await loadDataFromSheets(sheetId);
      // If sheet was recreated (deleted by user), update the sheet ID
      if (result.recreated && result.newSheetId) {
        setSheetId(result.newSheetId);
        // Load data from the new sheet
        await loadDataFromSheets(result.newSheetId);
      }
    } catch (err) {
      if (isAuthError(err)) {
        // Token expired — refresh via server and retry once
        await refreshGoogleToken();
        // Wait for token re-acquisition (up to 20s)
        let waited = 0;
        await new Promise<void>((resolve) => {
          const check = () => {
            waited += 500;
            if (isReady || waited >= 20000) { resolve(); return; }
            setTimeout(check, 500);
          };
          setTimeout(check, 1000); // Give it a moment to start
        });
        try {
          await loadDataFromSheets(sheetId);
        } catch (retryErr) {
          if (isGoogleReconnectError(retryErr) || googleTokenFailed) return;
          const errorMessage = retryErr instanceof Error ? retryErr.message : 'Failed to refresh data';
          setError(errorMessage);
        }
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to refresh data';
        setError(errorMessage);
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [sheetId, loadDataFromSheets, setSheetId, setError, isReady, refreshGoogleToken, googleTokenFailed]);

  const createSheet = useCallback(async () => {
    try {
      const newSheetId = await createNewSheet();
      setSheetId(newSheetId);
      return newSheetId;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create sheet';
      setError(errorMessage);
      throw err;
    }
  }, [createNewSheet, setSheetId, setError]);

  const initializeWithSheet = useCallback(async () => {
    if (!sheetId) {
      const newSheetId = await createSheet();
      await loadDataFromSheets(newSheetId);
    } else {
      const result = await loadDataFromSheets(sheetId);
      // If sheet was recreated (deleted by user), update the sheet ID
      if (result.recreated && result.newSheetId) {
        setSheetId(result.newSheetId);
        // Load data from the new sheet
        await loadDataFromSheets(result.newSheetId);
      }
    }
    setAppState('dashboard');
  }, [sheetId, createSheet, loadDataFromSheets, setSheetId, setAppState]);

  return {
    sheetId,
    isRefreshing,
    refreshData,
    createSheet,
    initializeWithSheet,
  };
};
